import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Stack,
  Paper,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
  Share as ShareIcon
} from '@mui/icons-material';

const ShareLocationModal = ({ 
  open, 
  onClose, 
  charger 
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const surface = isDark
    ? alpha(theme.palette.background.paper, 0.98)
    : theme.palette.common.white;
  const subtleBorder = isDark
    ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
    : '1px solid rgba(0, 0, 0, 0.06)';
  const panelGradient = isDark
    ? 'linear-gradient(135deg, rgba(10,12,24,0.98) 0%, rgba(4,7,17,0.94) 100%)'
    : 'linear-gradient(180deg, #ffffff 0%, #f7f2ff 40%, #eef6ff 100%)';
  const cardBorder = isDark
    ? `1px solid ${alpha(theme.palette.common.white, 0.12)}`
    : '1px solid rgba(0,0,0,0.08)';
  const cardHoverBg = isDark
    ? alpha(theme.palette.primary.main, 0.12)
    : '#f0f7ff';
  const copyGradient = 'linear-gradient(120deg, #5C7CFF 0%, #7D65FF 50%, #B64BFF 100%)';

  if (!charger) return null;

  // Generate Google Maps URL
  const lat = charger.location?.coordinates?.[1] || charger.locationLatitude || charger.lat;
  const lng = charger.location?.coordinates?.[0] || charger.locationLongitude || charger.lng;
  const googleMapsUrl = lat && lng ? 
    `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : 
    '';

  // Generate location text for sharing
  const locationName = charger.chargepoint_name || charger.name || 'Charging Station';
  const locationAddress = charger.street || charger.address || 'Address not available';
  const shareText = `${locationName} - ${locationAddress}`;

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(googleMapsUrl);
      setCopySuccess(true);
    } catch (err) {
    }
  };

  const handleSocialShare = (platform) => {
    const encodedUrl = encodeURIComponent(googleMapsUrl);
    const encodedText = encodeURIComponent(shareText);
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'gmail':
        shareUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${encodedText}&body=Check out this location: ${encodedUrl}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleClose = () => {
    setCopySuccess(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: isDark
              ? `0 24px 60px ${alpha(theme.palette.common.black, 0.85)}`
              : '0 8px 32px rgba(0, 0, 0, 0.12)',
            minHeight: 400,
            backgroundColor: surface,
            backgroundImage: panelGradient,
            border: subtleBorder,
            color: theme.palette.text.primary,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1,
          pt: 3
        }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem', color: theme.palette.text.primary }}>
            Share Location
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              backgroundColor: isDark
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.grey[200], 0.8),
              '&:hover': {
                backgroundColor: isDark
                  ? alpha(theme.palette.common.white, 0.15)
                  : alpha(theme.palette.grey[300], 0.9),
              }
            }}
          >
            <CloseIcon sx={{ color: theme.palette.text.secondary }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {/* Location Information */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.success.main, 
                fontWeight: 600, 
                mb: 1,
                fontSize: '1.1rem'
              }}
            >
              {locationName}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.95rem' }}
            >
              {locationAddress}
            </Typography>
          </Box>

          {/* Google Maps Link */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
              Google Maps Link
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                value={googleMapsUrl}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: {
                    backgroundColor: isDark
                      ? alpha(theme.palette.common.white, 0.06)
                      : 'grey.50',
                    fontSize: '0.9rem',
                    '& input': {
                      padding: '8px 12px'
                    }
                  }
                }}
              />
              <Tooltip title="Copy to clipboard">
                <IconButton
                  onClick={handleCopyToClipboard}
                  sx={{
                    backgroundColor: isDark
                      ? alpha(theme.palette.common.white, 0.08)
                      : 'grey.100',
                    '&:hover': {
                      backgroundColor: isDark
                        ? alpha(theme.palette.common.white, 0.15)
                        : 'grey.200'
                    }
                  }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Social Share Options */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2 }}>
              Share on Social Media
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              {/* Facebook */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: cardBorder,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  backgroundColor: isDark
                    ? alpha(theme.palette.common.white, 0.04)
                    : 'transparent',
                  '&:hover': {
                    borderColor: '#1877f2',
                    backgroundColor: cardHoverBg,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(24, 119, 242, 0.15)'
                  }
                }}
                onClick={() => handleSocialShare('facebook')}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#1877f2',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    mx: 'auto'
                  }}
                >
                  <FacebookIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="caption" fontWeight="500">
                  Facebook
                </Typography>
              </Paper>

              {/* Twitter/X */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: cardBorder,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  backgroundColor: isDark
                    ? alpha(theme.palette.common.white, 0.04)
                    : 'transparent',
                  '&:hover': {
                    borderColor: '#000000',
                    backgroundColor: isDark
                      ? alpha(theme.palette.common.white, 0.08)
                      : '#f5f5f5',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }
                }}
                onClick={() => handleSocialShare('twitter')}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#000000',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    mx: 'auto'
                  }}
                >
                  <TwitterIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="caption" fontWeight="500">
                  X (Twitter)
                </Typography>
              </Paper>

              {/* Gmail */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: cardBorder,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  backgroundColor: isDark
                    ? alpha(theme.palette.common.white, 0.04)
                    : 'transparent',
                  '&:hover': {
                    borderColor: '#ea4335',
                    backgroundColor: isDark
                      ? alpha(theme.palette.error.main, 0.08)
                      : '#fef7f7',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(234, 67, 53, 0.15)'
                  }
                }}
                onClick={() => handleSocialShare('gmail')}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#ea4335',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    mx: 'auto'
                  }}
                >
                  <EmailIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="caption" fontWeight="500">
                  Gmail
                </Typography>
              </Paper>
            </Stack>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: isDark
                  ? alpha(theme.palette.common.white, 0.2)
                  : undefined,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                }
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={handleCopyToClipboard}
              sx={{
                flex: 1,
                backgroundImage: copyGradient,
                color: theme.palette.common.white,
                '&:hover': { filter: 'brightness(1.05)' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark || theme.palette.primary.main, 0.35)}`
              }}
            >
              Copy Link
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setCopySuccess(false)} 
          severity="success"
          sx={{ 
            borderRadius: 2,
            backgroundColor: isDark
              ? alpha(theme.palette.success.dark, 0.85)
              : undefined,
            color: theme.palette.getContrastText(theme.palette.success.main)
          }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareLocationModal;
